/**
 * NCG A/B Testing System
 * Deterministic experiment assignment with Mixpanel integration
 *
 * Usage:
 *   - DOM-based: <div data-experiment="test_name"><div data-variant="control">...</div></div>
 *   - JS-based:  const variant = await NCGExperiments.getVariant('test_name');
 *   - Sync:      const variant = NCGExperiments.getVariantSync('test_name');
 */
(function() {
  'use strict';

  // Storage keys
  const STORAGE_KEY = 'ncg_exp_assignments';
  const EXPOSURE_KEY = 'ncg_exp_exposures';
  const SESSION_ID_KEY = 'ncg_exp_session_id';
  const CONFLICT_KEY = 'ncg_exp_anon_assignments'; // Temp storage for conflict detection
  const STORAGE_VERSION = 1;

  // ============================================================
  // HASHING FUNCTIONS
  // ============================================================

  /**
   * Compute SHA-256 hash and convert to percentage [0, 100)
   * Mirrors the C# backend implementation
   * @param {string} userId - User identifier
   * @param {string} experimentKey - Experiment name
   * @returns {Promise<number>} - Percentage [0, 100)
   */
  async function hashToPercent(userId, experimentKey) {
    const input = `${experimentKey}|${userId}`;
    const data = new TextEncoder().encode(input);

    try {
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = new Uint8Array(hashBuffer);

      // Take first 8 bytes and convert to BigInt (big-endian)
      let value = BigInt(0);
      for (let i = 0; i < 8; i++) {
        value = (value << BigInt(8)) | BigInt(hashArray[i]);
      }

      // Normalize to [0, 100): value / UInt64.MaxValue * 100
      const maxUint64 = BigInt('18446744073709551615');
      return Number((value * BigInt(10000)) / maxUint64) / 100;
    } catch (e) {
      // Fallback if SubtleCrypto fails
      return hashToPercentSync(userId, experimentKey);
    }
  }

  /**
   * Synchronous hash fallback (djb2 algorithm)
   * Used when SubtleCrypto is unavailable or for sync operations
   * @param {string} userId - User identifier
   * @param {string} experimentKey - Experiment name
   * @returns {number} - Percentage [0, 100)
   */
  function hashToPercentSync(userId, experimentKey) {
    const input = `${experimentKey}|${userId}`;
    let hash = 5381;
    for (let i = 0; i < input.length; i++) {
      hash = ((hash << 5) + hash) + input.charCodeAt(i);
      hash = hash >>> 0; // Convert to unsigned 32-bit
    }
    return (hash % 10000) / 100;
  }

  // ============================================================
  // STORAGE HELPERS
  // ============================================================

  /**
   * Safely get item from localStorage
   */
  function getStorage(key) {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Safely set item in localStorage
   */
  function setStorage(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Check if user is logged in (has Shopify customer ID)
   */
  function isLoggedIn() {
    return !!(window.NCG && window.NCG.customer && window.NCG.customer.id);
  }

  /**
   * Get Shopify customer ID if logged in
   */
  function getCustomerId() {
    if (isLoggedIn()) {
      return String(window.NCG.customer.id);
    }
    return null;
  }

  /**
   * Check if a userId looks like an anonymous ID (not a customer ID)
   */
  function isAnonymousId(userId) {
    if (!userId) return true;
    // Customer IDs are numeric, anonymous IDs have prefixes or special chars
    return userId.startsWith('$device:') ||
           userId.startsWith('anon_') ||
           userId.startsWith('temp_') ||
           userId.includes('-');  // UUIDs have dashes
  }

  /**
   * Get or create assignment storage
   * IMPORTANT: Logged-in customer ID always takes precedence.
   * If user was assigned while anonymous but is now logged in,
   * we invalidate and recompute to ensure cross-device consistency.
   */
  function getAssignmentStorage(userId) {
    const storage = getStorage(STORAGE_KEY);
    const customerId = getCustomerId();

    // If we have valid storage
    if (storage && storage.version === STORAGE_VERSION) {
      // If user is now logged in but was assigned while anonymous, invalidate
      if (customerId && isAnonymousId(storage.userId)) {
        // Save old anonymous assignments for conflict detection
        if (Object.keys(storage.assignments).length > 0) {
          setStorage(CONFLICT_KEY, {
            anonymousUserId: storage.userId,
            customerId: customerId,
            assignments: storage.assignments,
            savedAt: Date.now()
          });
        }

        // Clear assignments - will be recomputed with customer ID
        return {
          version: STORAGE_VERSION,
          userId: userId,
          assignments: {}
        };
      }

      // Update userId if changed
      if (storage.userId !== userId) {
        storage.userId = userId;
      }
      return storage;
    }

    // Create new storage
    return {
      version: STORAGE_VERSION,
      userId: userId,
      assignments: {}
    };
  }

  /**
   * Save assignment storage
   */
  function saveAssignmentStorage(storage) {
    setStorage(STORAGE_KEY, storage);
  }

  /**
   * Get or create exposure storage
   */
  function getExposureStorage() {
    const storage = getStorage(EXPOSURE_KEY);
    if (storage && storage.version === STORAGE_VERSION) {
      return storage;
    }
    return {
      version: STORAGE_VERSION,
      exposed: {}
    };
  }

  /**
   * Save exposure storage
   */
  function saveExposureStorage(storage) {
    setStorage(EXPOSURE_KEY, storage);
  }

  /**
   * Get or create session ID (persists in sessionStorage)
   */
  function getSessionId() {
    try {
      let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
      if (!sessionId) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem(SESSION_ID_KEY, sessionId);
      }
      return sessionId;
    } catch (e) {
      // Fallback if sessionStorage unavailable
      return 'session_fallback_' + Date.now();
    }
  }

  // ============================================================
  // COOKIE HELPERS
  // ============================================================

  /**
   * Set experiment cookie for server-side Liquid access
   */
  function setExperimentCookie(experimentKey, variant, expiryDays = 90) {
    try {
      const cookieName = `ncg_exp_${experimentKey}`;
      const expires = new Date(Date.now() + expiryDays * 864e5).toUTCString();
      document.cookie = `${cookieName}=${variant}; path=/; expires=${expires}; SameSite=Lax`;
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get experiment cookie value
   */
  function getExperimentCookie(experimentKey) {
    try {
      const cookieName = `ncg_exp_${experimentKey}`;
      const match = document.cookie.match(new RegExp('(^| )' + cookieName + '=([^;]+)'));
      return match ? match[2] : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Check if cookies can be set
   */
  function canSetCookies() {
    try {
      document.cookie = 'ncg_cookie_test=1; path=/; SameSite=Lax';
      const canSet = document.cookie.indexOf('ncg_cookie_test=1') !== -1;
      document.cookie = 'ncg_cookie_test=; Max-Age=0; path=/';
      return canSet;
    } catch (e) {
      return false;
    }
  }

  // ============================================================
  // USER ID
  // ============================================================

  /**
   * Get user ID for experiment assignment
   * Priority: Customer ID (if logged in) > Mixpanel ID > localStorage fallback
   * This ensures logged-in users always get consistent cohorts across devices.
   */
  function getUserId() {
    // If logged in, ALWAYS use customer ID for cross-device consistency
    const customerId = getCustomerId();
    if (customerId) {
      return customerId;
    }

    // For anonymous users, try Mixpanel distinct_id
    if (typeof mixpanel !== 'undefined' && typeof mixpanel.get_distinct_id === 'function') {
      const distinctId = mixpanel.get_distinct_id();
      if (distinctId) return distinctId;
    }

    // Fallback to localStorage-based ID
    try {
      let userId = localStorage.getItem('ncg_exp_user_id');
      if (!userId) {
        userId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('ncg_exp_user_id', userId);
      }
      return userId;
    } catch (e) {
      // Ultimate fallback - session-only ID
      return 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
  }

  // ============================================================
  // VARIANT ASSIGNMENT
  // ============================================================

  /**
   * Convert percentage to variant based on config
   * @param {number} percent - Percentage [0, 100)
   * @param {Object} variants - Variant allocation object { control: 50, variant: 50 }
   * @returns {string} - Variant name
   */
  function percentToVariant(percent, variants) {
    let cumulative = 0;
    for (const [variantName, allocation] of Object.entries(variants)) {
      cumulative += allocation;
      if (percent < cumulative) {
        return variantName;
      }
    }
    // Fallback to first variant
    return Object.keys(variants)[0] || 'control';
  }

  /**
   * Get experiment config
   */
  function getExperimentConfig(experimentKey) {
    return window.NCG?.experiments?.config?.experiments?.[experimentKey] || null;
  }

  /**
   * Get global settings
   */
  function getSettings() {
    return window.NCG?.experiments?.config?.settings || {
      exposureMode: 'once_per_session',
      debug: false
    };
  }

  /**
   * Check if experiment has server-side cookie requirement
   */
  function isServerSideExperiment(experimentKey) {
    const config = getExperimentConfig(experimentKey);
    return config?.serverSide === true;
  }

  // ============================================================
  // EXPOSURE TRACKING
  // ============================================================

  /**
   * Track experiment exposure to Mixpanel
   */
  function trackExposure(experimentKey, variant) {
    const settings = getSettings();
    const exposureMode = settings.exposureMode || 'once_per_session';
    const storage = getExposureStorage();
    const sessionId = getSessionId();

    let shouldTrack = false;
    const existingExposure = storage.exposed[experimentKey];

    switch (exposureMode) {
      case 'once_ever':
        shouldTrack = !existingExposure;
        break;

      case 'once_per_session':
        shouldTrack = !existingExposure || existingExposure.sessionId !== sessionId;
        break;

      case 'every_page':
        shouldTrack = true;
        break;

      default:
        shouldTrack = !existingExposure || existingExposure.sessionId !== sessionId;
    }

    if (shouldTrack) {
      // Fire Mixpanel event
      if (typeof mixpanel !== 'undefined' && typeof mixpanel.track === 'function') {
        mixpanel.track('experiment_exposed', {
          experiment_name: experimentKey,
          variant: variant,
          experiment_source: 'theme',
          exposure_ts: new Date().toISOString()
        });

        if (settings.debug) {
          console.log('[NCG Experiments] Tracked exposure:', experimentKey, variant);
        }
      }

      // Update storage
      storage.exposed[experimentKey] = {
        variant: variant,
        firstExposedAt: existingExposure?.firstExposedAt || Date.now(),
        sessionExposedAt: Date.now(),
        sessionId: sessionId
      };
      saveExposureStorage(storage);
    }
  }

  /**
   * Enrich Mixpanel with experiment super properties
   */
  function enrichMixpanelWithExperiments() {
    if (typeof mixpanel === 'undefined' || typeof mixpanel.register !== 'function') {
      return;
    }

    const userId = getUserId();
    const storage = getAssignmentStorage(userId);
    const experiments = window.NCG?.experiments?.config?.experiments || {};
    const superProperties = {};

    // Add each active experiment assignment as a super property
    Object.keys(experiments).forEach(experimentKey => {
      const config = experiments[experimentKey];
      if (config.enabled && storage.assignments[experimentKey]) {
        superProperties[`exp_${experimentKey}`] = storage.assignments[experimentKey].variant;
      }
    });

    if (Object.keys(superProperties).length > 0) {
      mixpanel.register(superProperties);

      if (getSettings().debug) {
        console.log('[NCG Experiments] Registered super properties:', superProperties);
      }
    }
  }

  /**
   * Check for and track cohort conflicts when user logs in
   * Fires 'experiment_cohort_conflict' event if anonymous assignment differs from logged-in assignment
   */
  function checkAndTrackCohortConflict(experimentKey, newVariant) {
    const conflictData = getStorage(CONFLICT_KEY);
    if (!conflictData) return;

    const oldAssignment = conflictData.assignments[experimentKey];
    if (!oldAssignment) return;

    const oldVariant = oldAssignment.variant;
    const hasConflict = oldVariant !== newVariant;

    // Fire Mixpanel event for conflict tracking
    if (typeof mixpanel !== 'undefined' && typeof mixpanel.track === 'function') {
      mixpanel.track('experiment_cohort_conflict', {
        experiment_name: experimentKey,
        anonymous_variant: oldVariant,
        logged_in_variant: newVariant,
        has_conflict: hasConflict,
        anonymous_user_id: conflictData.anonymousUserId,
        customer_id: conflictData.customerId,
        time_between_assignment_ms: Date.now() - (oldAssignment.assignedAt || conflictData.savedAt)
      });

      if (getSettings().debug) {
        console.log('[NCG Experiments] Cohort conflict check:', experimentKey,
          '| anonymous:', oldVariant, '| logged-in:', newVariant,
          '| conflict:', hasConflict);
      }
    }

    // Clean up: remove this experiment from conflict data
    delete conflictData.assignments[experimentKey];
    if (Object.keys(conflictData.assignments).length === 0) {
      // All experiments checked, remove conflict storage
      try {
        localStorage.removeItem(CONFLICT_KEY);
      } catch (e) {}
    } else {
      setStorage(CONFLICT_KEY, conflictData);
    }
  }

  // ============================================================
  // SERVER SYNC
  // ============================================================

  /**
   * Sync assignment with server for logged-in users
   * Implements "first touch wins" - server stores first assignment seen
   * @param {string} experimentKey - The experiment identifier
   * @param {string} currentVariant - The client-computed variant
   * @returns {Promise<string>} - The authoritative variant from server
   */
  async function syncWithServer(experimentKey, currentVariant) {
    const customerId = getCustomerId();
    if (!customerId) return { variant: currentVariant, synced: false }; // Anonymous, skip sync

    try {
      // Add timeout to prevent hanging if server doesn't respond
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch('https://www.nocompromisegaming.com/apps/apply/experiments/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify({
          CustomerId: customerId,
          ExperimentKey: experimentKey,
          CurrentVariant: currentVariant,
          AnonymousId: getMixpanelDistinctId()
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (getSettings().debug) {
          console.warn('[NCG Experiments] Server sync failed:', response.status);
        }
        return { variant: currentVariant, synced: false }; // Fallback to client assignment
      }

      const data = await response.json();

      if (getSettings().debug) {
        console.log('[NCG Experiments] Server sync:', experimentKey,
          '| variant:', data.variant, '| source:', data.source);
      }

      // Track if server returned different variant
      if (data.variant !== currentVariant) {
        trackServerOverride(experimentKey, currentVariant, data.variant);
      }

      return { variant: data.variant, synced: true };
    } catch (e) {
      if (getSettings().debug) {
        console.error('[NCG Experiments] Server sync error:', e);
      }
      return { variant: currentVariant, synced: false }; // Fallback to client assignment
    }
  }

  /**
   * Get Mixpanel distinct ID for debugging/analytics
   */
  function getMixpanelDistinctId() {
    if (typeof mixpanel !== 'undefined' && typeof mixpanel.get_distinct_id === 'function') {
      return mixpanel.get_distinct_id();
    }
    return null;
  }

  /**
   * Track when server overrides client assignment
   * Fires 'experiment_server_override' event for analytics
   */
  function trackServerOverride(experimentKey, clientVariant, serverVariant) {
    if (typeof mixpanel !== 'undefined' && typeof mixpanel.track === 'function') {
      mixpanel.track('experiment_server_override', {
        experiment_name: experimentKey,
        client_variant: clientVariant,
        server_variant: serverVariant
      });

      if (getSettings().debug) {
        console.log('[NCG Experiments] Server override:', experimentKey,
          '| client:', clientVariant, '| server:', serverVariant);
      }
    }
  }

  // ============================================================
  // MAIN API
  // ============================================================

  const NCGExperiments = {
    _ready: false,
    _readyCallbacks: [],

    /**
     * Get the variant for an experiment (async)
     * @param {string} experimentKey - The experiment identifier
     * @returns {Promise<string>} - The variant name
     */
    async getVariant(experimentKey) {
      const config = getExperimentConfig(experimentKey);

      // Return control if experiment doesn't exist or is disabled
      if (!config || !config.enabled) {
        return 'control';
      }

      const userId = getUserId();
      const storage = getAssignmentStorage(userId);

      // Check cache - but for logged-in users, still sync with server on first load
      if (storage.assignments[experimentKey]) {
        const cached = storage.assignments[experimentKey];
        // If already synced with server (or tried and failed), use cached value
        if (cached.source === 'server_sync' || cached.source === 'client_fallback' || !isLoggedIn()) {
          trackExposure(experimentKey, cached.variant);
          return cached.variant;
        }
        // Logged-in but not yet synced - fall through to sync
      }

      // Compute assignment
      const percent = await hashToPercent(userId, experimentKey);
      let variant = percentToVariant(percent, config.variants);

      // Sync with server for logged-in users (first touch wins)
      let source = 'client';
      if (isLoggedIn()) {
        // Check if we have a saved anonymous assignment (from before login)
        // This preserves the experience for new registrations
        const conflictData = getStorage(CONFLICT_KEY);
        const anonymousVariant = conflictData?.assignments?.[experimentKey]?.variant;
        const variantToSync = anonymousVariant || variant;

        if (anonymousVariant && getSettings().debug) {
          console.log('[NCG Experiments] Preserving anonymous assignment:', experimentKey, '→', anonymousVariant);
        }

        const syncResult = await syncWithServer(experimentKey, variantToSync);
        variant = syncResult.variant;
        source = syncResult.synced ? 'server_sync' : 'client_fallback';
      }

      // Cache assignment
      storage.assignments[experimentKey] = {
        variant: variant,
        assignedAt: Date.now(),
        percent: percent,
        source: source
      };
      saveAssignmentStorage(storage);

      // Set cookie for server-side experiments
      if (isServerSideExperiment(experimentKey)) {
        setExperimentCookie(experimentKey, variant);
      }

      // Check for cohort conflict (anonymous vs logged-in)
      checkAndTrackCohortConflict(experimentKey, variant);

      // Track exposure
      trackExposure(experimentKey, variant);

      // Update Mixpanel super properties
      enrichMixpanelWithExperiments();

      if (getSettings().debug) {
        console.log('[NCG Experiments] Assigned:', experimentKey, '→', variant, '(', percent.toFixed(2), '%)');
      }

      return variant;
    },

    /**
     * Get the variant for an experiment (sync)
     * Uses cached value or sync hash fallback
     * @param {string} experimentKey - The experiment identifier
     * @returns {string} - The variant name
     */
    getVariantSync(experimentKey) {
      const config = getExperimentConfig(experimentKey);

      if (!config || !config.enabled) {
        return 'control';
      }

      const userId = getUserId();
      const storage = getAssignmentStorage(userId);

      // Check cache
      if (storage.assignments[experimentKey]) {
        const cached = storage.assignments[experimentKey].variant;
        trackExposure(experimentKey, cached);
        return cached;
      }

      // Use sync hash
      const percent = hashToPercentSync(userId, experimentKey);
      const variant = percentToVariant(percent, config.variants);

      // Cache assignment
      storage.assignments[experimentKey] = {
        variant: variant,
        assignedAt: Date.now(),
        percent: percent
      };
      saveAssignmentStorage(storage);

      // Set cookie for server-side experiments
      if (isServerSideExperiment(experimentKey)) {
        setExperimentCookie(experimentKey, variant);
      }

      // Check for cohort conflict (anonymous vs logged-in)
      checkAndTrackCohortConflict(experimentKey, variant);

      // Track exposure
      trackExposure(experimentKey, variant);

      // Schedule async hash for verification (optional)
      this._verifyAssignmentAsync(experimentKey, userId, variant);

      if (getSettings().debug) {
        console.log('[NCG Experiments] Assigned (sync):', experimentKey, '→', variant, '(', percent.toFixed(2), '%)');
      }

      return variant;
    },

    /**
     * Verify sync assignment matches async hash (for debugging)
     * @private
     */
    async _verifyAssignmentAsync(experimentKey, userId, syncVariant) {
      const config = getExperimentConfig(experimentKey);
      if (!config) return;

      const asyncPercent = await hashToPercent(userId, experimentKey);
      const asyncVariant = percentToVariant(asyncPercent, config.variants);

      if (asyncVariant !== syncVariant && getSettings().debug) {
        console.warn('[NCG Experiments] Hash mismatch for', experimentKey,
          '- sync:', syncVariant, 'async:', asyncVariant);
      }
    },

    /**
     * Check if user is in a specific variant
     * @param {string} experimentKey - The experiment identifier
     * @param {string} variantName - The variant to check
     * @returns {boolean}
     */
    isVariant(experimentKey, variantName) {
      return this.getVariantSync(experimentKey) === variantName;
    },

    /**
     * Check if user is in control
     * @param {string} experimentKey - The experiment identifier
     * @returns {boolean}
     */
    isControl(experimentKey) {
      return this.isVariant(experimentKey, 'control');
    },

    /**
     * Initialize all DOM experiment elements
     * Looks for [data-experiment] containers and shows correct [data-variant]
     */
    async initializeExperimentElements() {
      const containers = document.querySelectorAll('[data-experiment]');

      for (const container of containers) {
        const experimentKey = container.dataset.experiment;
        const variant = await this.getVariant(experimentKey);

        // Hide all variants
        container.querySelectorAll('[data-variant]').forEach(el => {
          el.style.display = 'none';
        });

        // Show assigned variant
        const variantEl = container.querySelector(`[data-variant="${variant}"]`);
        if (variantEl) {
          variantEl.style.display = '';
        }

        // Add data attribute for CSS hooks
        container.dataset.activeVariant = variant;
      }
    },

    /**
     * Pre-initialize all server-side experiment cookies
     * Should be called early to ensure cookies are set before navigation
     */
    async initializeServerSideExperiments() {
      const experiments = window.NCG?.experiments?.config?.experiments || {};

      for (const [experimentKey, config] of Object.entries(experiments)) {
        if (config.enabled && config.serverSide) {
          // Get variant (this will set the cookie)
          await this.getVariant(experimentKey);
        }
      }
    },

    /**
     * Get all current assignments (for debugging)
     */
    getAllAssignments() {
      const userId = getUserId();
      const storage = getAssignmentStorage(userId);
      return storage.assignments;
    },

    /**
     * Clear all experiment data (for testing)
     */
    clearAll() {
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(EXPOSURE_KEY);
        localStorage.removeItem(CONFLICT_KEY);
        localStorage.removeItem('ncg_exp_user_id');
        sessionStorage.removeItem(SESSION_ID_KEY);
        console.log('[NCG Experiments] Cleared all experiment data');
      } catch (e) {
        console.error('[NCG Experiments] Failed to clear data:', e);
      }
    },

    /**
     * Register callback for when experiments are ready
     */
    onReady(callback) {
      if (this._ready) {
        callback();
      } else {
        this._readyCallbacks.push(callback);
      }
    },

    /**
     * Check if experiments system is ready
     */
    isReady() {
      return this._ready;
    },

    /**
     * Check if cookies are available
     */
    canUseCookies() {
      return canSetCookies();
    },

    /**
     * Get current user ID
     */
    getUserId() {
      return getUserId();
    }
  };

  // ============================================================
  // INITIALIZATION
  // ============================================================

  function init() {
    const settings = getSettings();
    let attempts = 0;
    const maxAttempts = 100; // 5 seconds at 50ms intervals

    // Wait for Mixpanel to be ready
    const checkMixpanel = setInterval(() => {
      attempts++;

      const mixpanelReady = typeof mixpanel !== 'undefined' &&
                            typeof mixpanel.get_distinct_id === 'function' &&
                            mixpanel.get_distinct_id();

      if (mixpanelReady || attempts >= maxAttempts) {
        clearInterval(checkMixpanel);

        if (settings.debug) {
          console.log('[NCG Experiments] Initialized',
            mixpanelReady ? '(Mixpanel ready)' : '(Mixpanel timeout, using fallback)');
        }

        // Initialize server-side experiment cookies
        NCGExperiments.initializeServerSideExperiments();

        // Initialize DOM elements
        NCGExperiments.initializeExperimentElements();

        // Enrich Mixpanel with experiment properties
        enrichMixpanelWithExperiments();

        // Mark as ready
        NCGExperiments._ready = true;

        // Fire ready callbacks
        NCGExperiments._readyCallbacks.forEach(cb => {
          try { cb(); } catch (e) { console.error(e); }
        });
        NCGExperiments._readyCallbacks = [];

        // Dispatch ready event
        document.dispatchEvent(new CustomEvent('ncg:experiments:ready'));
      }
    }, 50);
  }

  // Start initialization when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose globally
  window.NCGExperiments = NCGExperiments;

  // Debug helper
  if (getSettings().debug) {
    console.log('[NCG Experiments] Script loaded');
  }

})();
